import {EstimateAiEstimationStatusDto, EstimateDto, EstimateWithClientAndCostDto} from "../../shared/dtos/estimate.dto";
import {AddEstimateDto} from "../../shared/dtos/add-estimate.dto";
import {EstimateWithComponentsDto} from "../../shared/dtos/estimate-with-components.dto";
import {getUserTokenOrRedirect} from "../../shared/services/auth.service";
import {NavigateFunction} from "react-router-dom";
import {UpdateEstimateDto} from "../../shared/dtos/update-estimate.dto";
import {CustomerFacingEstimateDto} from "../../shared/dtos/customer-facing-estimate-data.dto";
import { UpdateEstimateStatusDto } from "../../shared/dtos/update-estimate-status.dto";
import { savaquanFetch, savaquanFetchJson } from "../../shared/services/http.service";
import { SavaquanHttpError } from "../../shared/utils/http-error";

export async function getEstimate(navigate: NavigateFunction, estimateId: number): Promise<EstimateWithComponentsDto> {
  const token = await getUserTokenOrRedirect(navigate)
  const estimate = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}`,
    {headers: {Authorization: `Bearer ${token}`}}).then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to fetch estimate: ${response.statusText}`);
      }
      return await response.json() as EstimateWithComponentsDto
  });
  return estimate;
}

export async function getEstimates(navigate: NavigateFunction): Promise<EstimateDto[]> {
  const token = await getUserTokenOrRedirect(navigate)
  const estimates = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates`,
    {headers: {Authorization: `Bearer ${token}`}})
    .then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to fetch estimates: ${response.statusText}`);
      }
      return await response.json() as EstimateDto[]
  });
  return estimates;
}

export async function getExtendedEstimates(navigate: NavigateFunction): Promise<EstimateWithClientAndCostDto[]> {
  const token = await getUserTokenOrRedirect(navigate)
  const estimates = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/extended`,
    {headers: {Authorization: `Bearer ${token}`}})
    .then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to fetch extended estimates: ${response.statusText}`);
      }
      return await response.json() as EstimateWithClientAndCostDto[]
    });
  return estimates;
}

export async function createEstimate(navigate: NavigateFunction, estimate: AddEstimateDto): Promise<EstimateWithComponentsDto> {
  const token = await getUserTokenOrRedirect(navigate)
  const createdEstimate = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(estimate)
    }).then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to create estimate: ${response.statusText}`);
      }
      return await response.json() as EstimateWithComponentsDto
  });
  return createdEstimate;
}

export async function updateEstimate(navigate: NavigateFunction, estimateId: number, estimate: UpdateEstimateDto): Promise<EstimateWithComponentsDto> {
  const token = await getUserTokenOrRedirect(navigate)
  const updatedEstimate = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(estimate)
    }).then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to update estimate: ${response.statusText}`);
      }
      return await response.json() as EstimateWithComponentsDto
  });
  return updatedEstimate;
}

export async function updateEstimateStatus(navigate: NavigateFunction, estimateId: number, updateEstimateStatusDto: UpdateEstimateStatusDto): Promise<EstimateWithComponentsDto> {
  const token = await getUserTokenOrRedirect(navigate)
  const updatedEstimate = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/status`,
    {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updateEstimateStatusDto)
    }).then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to update estimate status: ${response.statusText}`);
      }
      return await response.json() as EstimateWithComponentsDto
  });
  return updatedEstimate;
}

export async function getCustomerFacingEstimate(accessToken: string, estimateId: number): Promise<CustomerFacingEstimateDto> {
  const estimate = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/external/${estimateId}`,
    {headers: {Authorization: `Bearer ${accessToken}`}})
    .then(async response => {
      if (!response.ok) {
        // SQ-337: the status rides on the error; a deleted estimate answers 404.
        throw Object.assign(new Error(`Failed to fetch customer facing estimate: ${response.statusText}`), {status: response.status});
      }
      return await response.json() as CustomerFacingEstimateDto
    });
  return estimate;
}

export async function customerApproveEstimate(accessToken: string, estimateId: number, signatureFile: File): Promise<CustomerFacingEstimateDto> {
  if(!signatureFile) throw new Error('No signature provided')

  const formData = new FormData();
  formData.append('file', signatureFile);

  const estimate = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/external/${estimateId}/approve`,
    {
      method: 'POST',
      body: formData,
      headers: {Authorization: `Bearer ${accessToken}`}
    }).then(async response => {
      if (!response.ok) {
        throw new Error(`Failed to approve estimate: ${response.statusText}`);
      }
      return await response.json() as CustomerFacingEstimateDto
    });
  return estimate;
}


export async function getAiEstimationStatus(navigate: NavigateFunction, estimateId: number): Promise<EstimateAiEstimationStatusDto> {
  return savaquanFetchJson<EstimateAiEstimationStatusDto>(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/ai-estimation-status`,
    {
      navigate,
      method: 'GET',
    })
}

/**
 * SQ-283: an unapproved estimate opened for editing prices at the company's
 * CURRENT pricing rules. The server has no notion of edit mode, so the page
 * says so; it answers 204, or 409 from approval on (the record keeps the
 * rules the customer agreed to), which the caller treats as "nothing to do".
 */
export async function refreshEstimatePricingRules(navigate: NavigateFunction, estimateId: number): Promise<boolean> {
  try {
    await savaquanFetch(
      `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/pricing-rules/refresh`,
      { navigate, method: 'POST' },
    );
    return true;
  } catch (e) {
    if (e instanceof SavaquanHttpError && e.statusCode === 409) return false;
    throw e;
  }
}

/**
 * SQ-525: fresh AI suggestions for the estimate's current crew, priced at its
 * rates; they arrive over SignalR like any AI update. The server answers 400
 * when no crew is picked.
 */
export async function regenerateAiSuggestions(navigate: NavigateFunction, estimateId: number): Promise<void> {
  await savaquanFetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/ai/regenerate`,
    { navigate, method: 'POST' },
  );
}

/**
 * SQ-483: the customer's drawn signature, given in the company's app
 * (Customer Preview and Confirm). Records the signed document and approves
 * the estimate, which creates the job.
 */
export async function approveEstimateWithSignature(navigate: NavigateFunction, estimateId: number, signature: File): Promise<void> {
  const token = await getUserTokenOrRedirect(navigate);
  const formData = new FormData();
  formData.append('file', signature);
  const response = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/approve-with-signature`,
    {method: 'POST', body: formData, headers: {Authorization: `Bearer ${token}`}});
  if (!response.ok) {
    throw new Error(`Failed to approve estimate: ${response.statusText}`);
  }
}

/**
 * SQ-563 / SQ-745: one estimate's automatic follow-ups. The answer's stoppedAt
 * is when they were stopped, or null while they still go out.
 */
export async function getEstimateFollowUps(navigate: NavigateFunction, estimateId: number): Promise<{stoppedAt: string | null; companyFollowUpsEnabled?: boolean}> {
  return savaquanFetchJson<{stoppedAt: string | null; companyFollowUpsEnabled?: boolean}>(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/follow-ups`, {navigate});
}

/** Stops them (estimates.send; Pending Approval only). Sending the estimate again starts them. */
export async function stopEstimateFollowUps(navigate: NavigateFunction, estimateId: number): Promise<{stoppedAt: string | null}> {
  return savaquanFetchJson<{stoppedAt: string | null}>(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/estimates/${estimateId}/follow-ups/stop`, {navigate, method: 'POST'});
}
